// Centralized API helpers for Lift Admin frontend - Pantry
// This file aggregates all REST endpoints in a single place so other features can import a typed helper instead
// of sprinkling axios calls throughout the codebase.

import apiClient from '@lift-admin/utils/apiClient';
import { Toastify } from 'lift-ui';
import { getUserId } from '@lift-admin/utils';
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import axios from 'axios';

// ---------------  Environment Switch --------------------------------------
// Toggle this flag to switch all API calls between localhost (dev) and the
// production reverse-proxied "/buddies" prefix.  When `USE_LOCAL_BACKEND` is
// true the API_BASE becomes "http://localhost:8081" (make sure the backend is
// running locally).  When false it defaults to using the relative "/buddies"
// path so requests are proxied by the frontend server.

const USE_LOCAL_BACKEND = false; // <-- set to true when developing locally
const API_BASE = USE_LOCAL_BACKEND ? 'http://localhost:8081' : '/buddies';

/** Build the final URL by concatenating the base with the path */
const apiUrl = (path: string) => `${API_BASE}${path}`;

export interface PantryItem {
  id: number;
  file_name: string;
  metadata: {
    title?: string;
    description?: string;
    isUrl?: boolean;
    [key: string]: any;
  } | null;
  source_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Get all pantry items
 * GET /api/pantry/
 */
export async function getAllPantryItems(skip = 0, limit = 100): Promise<PantryItem[]> {
  try {
    const response = await apiClient.get(apiUrl('/api/pantry/'), {
      params: { skip, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching pantry items:', error);
    Toastify('error', 'Failed to fetch pantry items');
    throw error;
  }
}

/**
 * Upload multiple files and URLs to pantry with individual metadata
 * Handles both file uploads and URL submissions in a single request
 */
export async function uploadPantryFiles(
  filesWithMetadata: Array<{ 
    file?: File; 
    url?: string; 
    title: string; 
    description: string; 
    isUrl: boolean 
  }>
): Promise<PantryItem[]> {
  try {
    const formData = new FormData();
    
    // Add all items to form data with proper indexing
    filesWithMetadata.forEach((item, index) => {
      if (item.isUrl && item.url) {
        // For URLs, add the URL and metadata
        formData.append(`urls[${index}]`, item.url);
        formData.append(`metadata[${index}]`, JSON.stringify({
          title: item.title,
          description: item.description,
          isUrl: true,
        }));
      } else if (!item.isUrl && item.file) {
        // For files, add the file and metadata
        formData.append('files', item.file);
        formData.append(`metadata[${index}]`, JSON.stringify({
          title: item.title,
          description: item.description,
          isUrl: false,
        }));
      }
    });

    const response = await apiClient.post(
      apiUrl('/api/pantry/upload-multiple'),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    
    console.log('📦 API upload response:', response.data);
    
    const fileCount = filesWithMetadata.filter(item => !item.isUrl).length;
    const urlCount = filesWithMetadata.filter(item => item.isUrl).length;
    
    let successMessage = '';
    if (fileCount > 0 && urlCount > 0) {
      successMessage = `${fileCount} file(s) and ${urlCount} URL(s) uploaded successfully`;
    } else if (fileCount > 0) {
      successMessage = `${fileCount} file(s) uploaded successfully`;
    } else {
      successMessage = `${urlCount} URL(s) submitted successfully`;
    }
    
    Toastify('success', successMessage);
    await logAdminAction(`Pantry upload: ${fileCount} file(s), ${urlCount} URL(s)`, {
      url: apiUrl('/api/pantry/upload-multiple'),
      method: 'POST',
      status: 200,
      category: 'settings',
      action: 'upload_pantry_items',
      fileCount,
      urlCount,
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading files/URLs:', error);
    Toastify('error', 'Failed to upload files/URLs');
    await logAdminAction(
      `Failed to upload pantry items: ${(error as any)?.response?.data?.detail || (error as any)?.message || 'Unknown error'}`,
      { url: apiUrl('/api/pantry/upload-multiple'), method: 'POST', category: 'settings', action: 'upload_pantry_items' },
      true
    );
    throw error;
  }
}

/**
 * Delete a pantry item
 * DELETE /api/pantry/{id}
 */
export async function deletePantryItem(id: number): Promise<void> {
  try {
    await apiClient.delete(apiUrl(`/api/pantry/${id}`));
    Toastify('success', 'Pantry item deleted successfully');
    await logAdminAction(`Pantry item deleted (ID ${id})`, {
      url: apiUrl(`/api/pantry/${id}`),
      method: 'DELETE',
      status: 200,
      category: 'settings',
      action: 'delete_pantry_item',
      pantryItemId: id,
    });
  } catch (error) {
    console.error('Error deleting pantry item:', error);
    Toastify('error', 'Failed to delete pantry item');
    throw error;
  }
}

/**
 * Bulk delete multiple pantry items
 * POST /api/pantry/bulk-delete
 */
export async function bulkDeletePantryItems(ids: number[]): Promise<{
  deleted: number[];
  failed: Array<{ id: number; error: string; status: string }>;
  message: string;
}> {
  try {
    const response = await apiClient.post(apiUrl('/api/pantry/bulk-delete'), {
      ids: ids
    });
    
    const { deleted, failed } = response.data;
    const deletedCount = deleted.length;
    const failedCount = failed.length;
    
    if (failedCount === 0) {
      Toastify('success', `${deletedCount} item${deletedCount !== 1 ? 's' : ''} deleted successfully`);
    } else if (deletedCount === 0) {
      Toastify('error', `Failed to delete ${failedCount} item${failedCount !== 1 ? 's' : ''}`);
    } else {
      Toastify('warning', `${deletedCount} items deleted, ${failedCount} failed`);
    }
    await logAdminAction(`${deletedCount} pantry item(s) deleted (bulk)`, {
      url: apiUrl('/api/pantry/bulk-delete'),
      method: 'POST',
      status: 200,
      category: 'settings',
      action: 'bulk_delete_pantry_items',
      deletedCount,
      failedCount,
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk deleting pantry items:', error);
    Toastify('error', 'Failed to delete selected items');
    throw error;
  }
}

/**
 * Update a pantry item
 * PUT /api/pantry/{id}
 */
export async function updatePantryItem(
  id: number,
  updates: { metadata?: any; source_url?: string },
  previous?: { metadata?: any; source_url?: string }
): Promise<PantryItem> {
  try {
    const response = await apiClient.put(apiUrl(`/api/pantry/${id}`), updates);
    Toastify('success', 'Pantry item updated successfully');

    // Build before -> after changes when the caller provides the previous state.
    const changes: Array<{ label: string; from?: string; to?: string }> = [];
    const stringify = (val: any) =>
      val === undefined || val === null
        ? '—'
        : typeof val === 'object'
          ? JSON.stringify(val)
          : String(val);
    if (previous) {
      if ('source_url' in updates && previous.source_url !== updates.source_url) {
        changes.push({ label: 'Source URL', from: stringify(previous.source_url), to: stringify(updates.source_url) });
      }
      if ('metadata' in updates && JSON.stringify(previous.metadata) !== JSON.stringify(updates.metadata)) {
        changes.push({ label: 'Metadata', from: stringify(previous.metadata), to: stringify(updates.metadata) });
      }
    }
    const changeSummary = changes.map(c => `${c.label}: ${c.from} → ${c.to}`).join('; ');
    await logAdminAction(`Pantry item updated (ID ${id})${changeSummary ? ` — ${changeSummary}` : ''}`, {
      url: apiUrl(`/api/pantry/${id}`),
      method: 'PUT',
      status: 200,
      category: 'settings',
      action: 'update_pantry_item',
      pantryItemId: id,
      ...(changes.length ? { changes } : { updatedFields: Object.keys(updates || {}) }),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating pantry item:', error);
    Toastify('error', 'Failed to update pantry item');
    throw error;
  }
}

// ----------  Sort Order  ------------------------------------------------

export interface PantrySortOrderItem {
  id: number;
  sort_order: number;
}

export interface PantrySortOrderRequest {
  entity_type: 'pantry';
  items: PantrySortOrderItem[];
}

export interface PantrySortOrderResult {
  id: number;
  success: boolean;
  sort_order: number;
  error: string | null;
}

export interface PantrySortOrderResponse {
  entity_type: 'pantry';
  total: number;
  updated: number;
  failed: number;
  results: PantrySortOrderResult[];
  message: string;
}

/**
 * Update sort order for pantry items
 * PUT /api/v1/sort-order/
 */
export async function updatePantrySortOrder(items: PantrySortOrderItem[]): Promise<PantrySortOrderResponse> {
  try {
    const payload: PantrySortOrderRequest = {
      entity_type: 'pantry',
      items
    };
    
    const response = await apiClient.put<PantrySortOrderResponse>(apiUrl('/api/v1/sort-order/'), payload);
    
    if (response.data.failed > 0) {
      Toastify('warning', `Updated ${response.data.updated} pantry items, ${response.data.failed} failed`);
    } else {
      Toastify('success', response.data.message);
    }
    await logAdminAction(`Pantry sort order updated (${response.data.updated} item(s))`, {
      url: apiUrl('/api/v1/sort-order/'),
      method: 'PUT',
      status: 200,
      category: 'settings',
      action: 'reorder_pantry_items',
      updated: response.data.updated,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating pantry sort order:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to update pantry order';
    Toastify('error', errorMessage);
    throw error;
  }
}
